import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader/PageHeader';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { OrganizationProducts } from '../components/Customer/OrganizationProducts';
import { DateFormat } from '../helpers/dateFormat';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { ConfirmationDialog } from '../components/ConfirmationDialog/ConfirmationDialog';
import { OrganizationUsers } from '../components/Customer/OrganizationUsers';
import { CustomerTickets } from '../components/Customer/CustomerTickets';
import {
  OrganizationAvailableProductsFetcher,
  OrganizationProductsFetcher,
  CustomerTicketsFetcher,
  CustomerUsersFetcher,
  OrganizationFetcher,
  OrganizationScheduledProductsFetcher,
} from '../components/Customer/CustomerFetchers';
import { useAutoFetch } from '../contexts/Fetcher';
import { countries } from '../helpers/countries';
import { CustomerLive } from '../components/Customer/CustomerLive';
import { Trans, useTranslation } from 'react-i18next';
import { Date } from '../components/Date/Date';
import { AccountSingleData } from '../components/AccountData/AccountSingleData';
import Box from '@mui/system/Box';
import { useDialog } from '../hooks/useDialog';
import { EditOrganizationDialog } from '../components/EditOrganizationDialog/EditOrganizationDialog';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { apiOrganization } from '../resources/api/apiOrganization';
import { useDecodedParams } from '../hooks/useDecodedParams';
import { useIsGuardedRouteAllowed } from '../resources/GuardedRouteLink';
import { matchPath } from 'react-router';
import { OrganizationInvites } from '../components/Customer/OrganizationInvites';
import { OrganizationInvitesFetcher } from '../fetchers/OrganizationInvitesFetcher';
import {
  EOrganizationSelectionStatus,
  IOrganizationContext,
  OrganizationContext,
} from '../contexts/OrganizationContext';
import { OrganizationScheduledProducts } from '../components/Customer/OrganizationScheduledProducts';
import { LazyUser } from '../components/LazyData/LazyUser';
import { NoOrganizationContextProvider } from '../contexts/OrganizationContextProvider';
import { apiZendesk } from '../resources/api/apiZendesk';
import { NotificationContext } from '../contexts/NotificationContext';

export type CustomerTab = 'products' | 'users' | 'invites' | 'tickets' | 'live';
export interface IOrganizationUrlParams {
  organization_name: string;
  tab?: CustomerTab;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    buttons: {
      display: 'flex',
      gap: theme.spacing(1),
    },
  })
);

const { useArchiveOrganizationByName, useUpdateOrganizationByName } = apiOrganization;
const { useReassignOrganization } = apiZendesk;

const tabs: Array<{ tab: CustomerTab; route: (organization_name?: string) => string }> = [
  {
    tab: 'products',
    route: AuthenticatedRoutes.OrganizationDetailsProducts,
  },
  {
    tab: 'users',
    route: AuthenticatedRoutes.OrganizationDetailsUsers,
  },
  {
    tab: 'tickets',
    route: AuthenticatedRoutes.OrganizationDetailsTickets,
  },
  {
    tab: 'live',
    route: AuthenticatedRoutes.OrganizationDetailsLive,
  },
  {
    tab: 'invites',
    route: AuthenticatedRoutes.OrganizationDetailsInvites,
  },
];

const ProductsTab: FC = () => {
  return (
    <>
      <OrganizationScheduledProducts defaultExpanded={true} />
      <OrganizationProducts />
    </>
  );
};

export const OrganizationPage: FC = () => {
  const styles = useStyles();
  const { data, onRefresh } = useContext(OrganizationFetcher.Context);
  const { addNotification } = useContext(NotificationContext);

  const { pathname } = useLocation();

  const { organization_name } = useDecodedParams<IOrganizationUrlParams>();

  const tab = tabs.find((tab) => matchPath(pathname, tab.route()))?.tab;

  const history = useHistory();
  const handleApiError = useApiErrorHandler();

  const { execute: archiveOrganization, Guard: GuardArchiveOrganization } =
    useArchiveOrganizationByName(organization_name);
  const { Guard: GuardEdit } = useUpdateOrganizationByName(organization_name);
  const { Guard: GuardReassignZendesk, execute: reassignZendesk } = useReassignOrganization(organization_name);

  const { isOpen: isEdit, openUrl: editDialogUrl, close: closeDialog } = useDialog('edit');
  const { isOpen: isArchive, openUrl: archiveDialogUrl } = useDialog('archive');

  const { t } = useTranslation();

  const isProductsAllowed = useIsGuardedRouteAllowed('OrganizationDetailsProducts', [organization_name]);
  const isUsersAllowed = useIsGuardedRouteAllowed('OrganizationDetailsUsers', [organization_name]);
  const isInvitesAllowed = useIsGuardedRouteAllowed('OrganizationDetailsInvites', [organization_name]);
  const isTicketsAllowed = useIsGuardedRouteAllowed('OrganizationDetailsTickets', [organization_name]);
  const isLiveAllowed = useIsGuardedRouteAllowed('OrganizationDetailsLive', [organization_name]);

  const setActiveTab = useCallback(
    (tab: CustomerTab, replaceState = false) => {
      const route = tabs.find((tabDetails) => tabDetails.tab === tab)?.route;

      if (route) {
        const url = route(organization_name);
        history[replaceState ? 'replace' : 'push'](url);
      }
    },
    [organization_name]
  );

  const onTabChange = useCallback(
    (_, tab) => {
      setActiveTab(tab);
    },
    [setActiveTab]
  );

  useAutoFetch(OrganizationFetcher, void 0, data?.payload.name !== organization_name);

  useEffect(() => {
    if (!tab) {
      setActiveTab('products', true);
    }
  }, [setActiveTab, tab]);

  const onReassignZendeskId = useCallback(async () => {
    try {
      await reassignZendesk();
      addNotification({
        severity: 'success',
        message: t('organization.reassignZendeskIdSucceeded'),
      });
      await onRefresh();
    } catch (e) {
      handleApiError(e);
    }
  }, [reassignZendesk, onRefresh]);

  const onArchive = useCallback(async () => {
    try {
      await archiveOrganization();
      history.push(AuthenticatedRoutes.Organizations());
    } catch (e) {
      handleApiError(e);
    }
  }, []);

  const orgCtxProv = useMemo<IOrganizationContext>(
    () => ({
      selectionStatus: EOrganizationSelectionStatus.Injected,
      organization: data?.payload ?? null,
      organizationId: data?.payload.id ?? null,
      setOrganization: () => {
        throw new Error('cannot be performed in this context');
      },
    }),
    [data]
  );

  if (!data) return null;
  return (
    <div>
      <PageHeader
        title={data.payload.name}
        crumbs={[
          {
            url: AuthenticatedRoutes.Organizations(),
            title: t('layout.navigation.organizations'),
          },
        ]}
      >
        <div className={styles.buttons}>
          {!data.payload.archived ? (
            <GuardArchiveOrganization>
              <Button component={RouterLink} to={archiveDialogUrl} variant="outlined" replace={true}>
                {t('common.button.archive')}
              </Button>
            </GuardArchiveOrganization>
          ) : null}

          <GuardEdit>
            <Button component={RouterLink} to={editDialogUrl} variant="contained" color="primary" replace={true}>
              {t('common.button.edit')}
            </Button>
          </GuardEdit>

          {/*<GuardDeleteOrganization>*/}
          {/*  <ConditionalTooltip*/}
          {/*    enabled={false /*productsData.customerProducts.length > 0*!/*/}
          {/*    title={t('organization.cannotDelete') as string}*/}
          {/*  >*/}
          {/*    <span>*/}
          {/*      <DangerousButton*/}
          {/*        component={RouterLink}*/}
          {/*        to={deleteDialogUrl}*/}
          {/*        variant="contained"*/}
          {/*        // @todo disable when products are assigned*/}
          {/*        disabled={false /*productsData.customerProducts.length > 0*!/*/}
          {/*      >*/}
          {/*        {t('common.button.delete')}*/}
          {/*      </DangerousButton>*/}
          {/*    </span>*/}
          {/*  </ConditionalTooltip>*/}
          {/*</GuardDeleteOrganization>*/}
        </div>
      </PageHeader>

      <Grid container spacing={1}>
        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.id')}>{data.payload.id}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.organization')}>{data.payload.name}</AccountSingleData>
        </Grid>

        {/*<Grid item xs={6} md={4} lg={3}>*/}
        {/*  <AccountSingleData label={t('common.tableHeader.domain')}>-- not implemented --</AccountSingleData>*/}
        {/*</Grid>*/}

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.owner')}>
            {data.payload.owner_id ? <LazyUser id={data.payload.owner_id} /> : '---'}
          </AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.pixotopeRepresentative')}>
            <NoOrganizationContextProvider>
              {data.payload.pixotope_representative ? <LazyUser id={data.payload.pixotope_representative} /> : 'none'}
            </NoOrganizationContextProvider>
          </AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.address')}>{data.payload.address}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.billingAddress')}>{data.payload.billing_address}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.country')}>
            {data.payload.country} {countries.find((country) => country.code === data.payload.country)?.name}
          </AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.lastUpdated')}>
            <Date date={data.payload.updated_at} format={DateFormat.DateWithFullTime} />
          </AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.zenDesk')}>
            {data.payload.zendesk_id ?? '-'}
            <GuardReassignZendesk>
              {' '}
              <Button onClick={onReassignZendeskId} variant="contained" color="secondary" size="small">
                {t('organization.reassignZendeskId')}
              </Button>
            </GuardReassignZendesk>
          </AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.hubSpot')}>{data.payload.hubspot_id}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.notes')}>{data.payload.notes}</AccountSingleData>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={onTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {isProductsAllowed ? <Tab label={t('organization.products.tabTitle')} value="products" /> : null}
          {isUsersAllowed ? <Tab label={t('organization.users.tabTitle')} value="users" /> : null}
          {isInvitesAllowed ? <Tab label={t('organization.invites.tabTitle')} value="invites" /> : null}
          {isTicketsAllowed ? <Tab label={t('organization.tickets.tabTitle')} value="tickets" /> : null}
          {isLiveAllowed ? <Tab label={t('organization.live.tabTitle')} value="live" /> : null}
        </Tabs>
      </Box>

      <OrganizationContext.Provider value={orgCtxProv}>
        {tab === 'products' ? (
          <OrganizationProductsFetcher.WMF>
            <OrganizationScheduledProductsFetcher.WMF>
              <OrganizationAvailableProductsFetcher.WMF>
                <ProductsTab />
              </OrganizationAvailableProductsFetcher.WMF>
            </OrganizationScheduledProductsFetcher.WMF>
          </OrganizationProductsFetcher.WMF>
        ) : null}
        {tab === 'users' ? (
          <CustomerUsersFetcher.WMF>
            <OrganizationUsers />
          </CustomerUsersFetcher.WMF>
        ) : null}
        {tab === 'tickets' ? (
          <CustomerTicketsFetcher.WMF>
            <CustomerTickets />
          </CustomerTicketsFetcher.WMF>
        ) : null}
        {tab === 'live' ? <CustomerLive /> : null}
        {tab === 'invites' ? (
          <OrganizationInvitesFetcher.WMF>
            <OrganizationInvites />
          </OrganizationInvitesFetcher.WMF>
        ) : null}
      </OrganizationContext.Provider>

      {/*<ConfirmationDialog*/}
      {/*  open={isDelete}*/}
      {/*  title={t('organization.deleteDialog.title')}*/}
      {/*  message={t('organization.deleteDialog.message', { organization: data.payload.name })}*/}
      {/*  confirm={onDelete}*/}
      {/*  abort={closeDialog}*/}
      {/*  dangerous*/}
      {/*  confirmLabel={t('common.button.delete')}*/}
      {/*/>*/}
      <EditOrganizationDialog open={isEdit} closeDialog={closeDialog} />
      <GuardArchiveOrganization>
        <ConfirmationDialog
          open={isArchive}
          title={t('organization.archiveDialog.titleArchive')}
          confirm={onArchive}
          abort={closeDialog}
          confirmLabel={t('organization.archiveDialog.confirmArchive')}
        >
          <Trans i18nKey="organization.archiveDialog.contentArchive">
            <Typography paragraph />
            <List>
              <ListItem />
              <ListItem />
            </List>
            <Typography paragraph />
          </Trans>
        </ConfirmationDialog>
      </GuardArchiveOrganization>
    </div>
  );
};
